import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { LoaderCircle, ShieldX } from 'lucide-react';
import { useAuth } from './auth/context';
import { PERMISSIONS } from './auth/permissions';
import { canAll } from './auth/rbac';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import type { Permission, Role, Session } from './types';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Uploads = lazy(() => import('./pages/Uploads'));
const DataSources = lazy(() => import('./pages/DataSources'));
const ExploreDatasets = lazy(() => import('./pages/ExploreDatasets'));
const VaultDatasets = lazy(() => import('./pages/VaultDatasets'));
const RetailUpload = lazy(() => import('./pages/RetailUpload'));
const RetailDatasets = lazy(() => import('./pages/RetailDatasets'));
const Processing = lazy(() => import('./pages/Processing'));
const Users = lazy(() => import('./pages/Users'));
const Clients = lazy(() => import('./pages/Clients'));
const UserAccess = lazy(() => import('./pages/UserAccess'));
const Permissions = lazy(() => import('./pages/Permissions'));
const Profile = lazy(() => import('./pages/Profile'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'));
const FreelancerPortal = lazy(() => import('./pages/FreelancerPortal'));
const AcquisitionSuperadminHub = lazy(() => import('./pages/AcquisitionSuperadminHub'));
const QcReviewWorkbench = lazy(() => import('./pages/QcReviewWorkbench'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const OtpVerification = lazy(() => import('./pages/OtpVerification'));

export default function App() {
  const { loading, session, logout } = useAuth();
  if (loading) return <div className="page-loader full-page"><LoaderCircle className="spin" /> Restoring secure session…</div>;
  if (!session) {
    return <Suspense fallback={<div className="page-loader full-page"><LoaderCircle className="spin" /> Loading…</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-otp" element={<OtpVerification />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>;
  }
  const isRetailClient = Boolean(
    session.isRetailWorkspace && !session.roles?.some((role) => ['superadmin', 'qc'].includes(role.toLowerCase())),
  );
  return (
    <AppShell session={session} onLogout={logout}>
      <Suspense fallback={<div className="page-loader"><LoaderCircle className="spin" /> Loading workspace…</div>}>
        <ErrorBoundary fallbackTitle="Data Vault workspace error">
          <Routes>
            <Route
              path="/"
              element={
                session.roles?.includes('freelancer') ? (
                  <Navigate to="/freelancer/portal" replace />
                ) : session.roles?.includes('qc') ? (
                  <Navigate to="/acquisition/qc-review" replace />
                ) : isRetailClient ? (
                  <Navigate to="/retail-datasets" replace />
                ) : (
                  <Protected session={session} permission={[PERMISSIONS.DASHBOARD_READ, PERMISSIONS.JOB_READ]}>
                    <Dashboard />
                  </Protected>
                )
              }
            />
            <Route path="/freelancer/portal" element={<Protected session={session} roles={['freelancer']} permission={PERMISSIONS.SUBMISSION_READ_MINE}><FreelancerPortal /></Protected>} />
            <Route path="/acquisition/superadmin" element={<Protected session={session} roles={['superadmin']} permission={PERMISSIONS.SUBMISSION_SUPERADMIN_READ}><AcquisitionSuperadminHub /></Protected>} />
            <Route path="/acquisition/client-files" element={<Protected session={session} roles={['superadmin']} permission={PERMISSIONS.SUBMISSION_SUPERADMIN_READ}><AcquisitionSuperadminHub submissionKind="client" /></Protected>} />
            <Route path="/acquisition/qc-review" element={<Protected session={session} roles={['qc', 'superadmin']} permission={PERMISSIONS.SUBMISSION_QC_REVIEW}><QcReviewWorkbench /></Protected>} />
            <Route path="/uploads" element={<Protected session={session} permission={PERMISSIONS.DATASET_CREATE}><Uploads /></Protected>} />
            <Route path="/datasets" element={<Navigate to="/vault-datasets" replace />} />
            <Route path="/data-sources" element={<Protected session={session} permission={PERMISSIONS.SOURCE_READ}><DataSources /></Protected>} />
            <Route path="/explore-datasets" element={<Protected session={session} permission={PERMISSIONS.CLIENT_CATALOG_READ}><ExploreDatasets /></Protected>} />
            <Route path="/vault-datasets" element={<Protected session={session} permission={PERMISSIONS.VAULT_READ}><VaultDatasets /></Protected>} />
            <Route path="/retail-datasets" element={<Protected session={session} requireRetail><RetailUpload /></Protected>} />
            <Route path="/retail-datasets/pipeline" element={<Protected session={session} requireRetail><RetailDatasets /></Protected>} />
            <Route path="/processing" element={<Protected session={session} permission={PERMISSIONS.JOB_READ}><Processing /></Protected>} />
            <Route path="/users" element={<Protected session={session} roles={['superadmin', 'admin', 'manager']} permission={PERMISSIONS.USER_READ}><Users /></Protected>} />
            <Route path="/clients" element={<Protected session={session} roles={['superadmin']} permission={PERMISSIONS.USER_READ}><Clients /></Protected>} />
            <Route path="/user-access" element={<Protected session={session} roles={['superadmin', 'admin', 'manager']} permission={[PERMISSIONS.USER_READ, PERMISSIONS.USER_UPDATE]}><UserAccess /></Protected>} />
            <Route path="/user-access/new" element={<Protected session={session} roles={['superadmin', 'admin', 'manager']} permission={[PERMISSIONS.USER_READ, PERMISSIONS.USER_UPDATE, PERMISSIONS.USER_CREATE, PERMISSIONS.ROLE_MANAGE]}><UserAccess startCreating /></Protected>} />
            <Route path="/user-access/new-client" element={<Protected session={session} roles={['superadmin']} permission={[PERMISSIONS.USER_READ, PERMISSIONS.USER_UPDATE, PERMISSIONS.USER_CREATE, PERMISSIONS.ROLE_MANAGE]}><UserAccess startCreating initialRole="admin" /></Protected>} />
            <Route path="/permissions" element={<Protected session={session} roles={['superadmin', 'admin']} permission={PERMISSIONS.PERMISSION_READ}><Permissions /></Protected>} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings/account" element={<AccountSettings />} />
            <Route path="/settings/security" element={<SecuritySettings />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/settings/security" replace />} />
            <Route path="/reset-password" element={<Navigate to="/settings/security" replace />} />
            <Route path="/verify-otp" element={<Navigate to="/settings/security" replace />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    </AppShell>
  );
}

function Protected({
  session,
  permission,
  roles,
  requireRetail,
  children,
}: {
  session: Session;
  permission?: Permission | Permission[];
  roles?: Role[];
  requireRetail?: boolean;
  children: ReactNode;
}) {
  if (requireRetail) {
    const hasRetailAccess = Boolean(
      session.isRetailWorkspace
      || session.roles?.some((role) => ['superadmin', 'qc'].includes(role.toLowerCase()))
      || session.permissions?.some((permissionKey) => permissionKey.startsWith('retail:')),
    );
    if (!hasRetailAccess) {
      return (
        <div className="page">
          <div className="access-denied">
            <ShieldX />
            <h2>Retail Vision access required</h2>
            <p>Your workspace is not configured for the Retail Dataset platform.</p>
          </div>
        </div>
      );
    }
  }
  if (roles && !roles.some((r) => session.roles?.includes(r))) {
    return (
      <div className="page">
        <div className="access-denied">
          <ShieldX />
          <h2>Access restricted</h2>
          <p>Your role does not allow access to this area.</p>
        </div>
      </div>
    );
  }
  if (!permission || canAll(session, Array.isArray(permission) ? permission : [permission])) return children;
  return (
    <div className="page">
      <div className="access-denied">
        <ShieldX />
        <h2>Access restricted</h2>
        <p>Your role does not allow access to this area.</p>
      </div>
    </div>
  );
}
